import { EmptyState } from "@/components/molecules/empty-state";
import { ErrorState } from "@/components/molecules/error-state";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAgents } from "@/features/agents/hooks/use-agents";
import { useCurrentClient } from "@/features/clients/hooks/use-clients";
import { useConnectors } from "@/features/connectors/hooks/use-connectors";
import { useTranslation } from "@/features/utils/i18n";
import { PhoneIcon, PlusIcon, RobotIcon } from "@phosphor-icons/react";
import { useMemo, useState } from "react";
import {
  ConnectorContainer,
  ConnectorElement,
} from "@/components/ui/connector";
import { Link } from "react-router-dom";
import { PageLayout } from "../layout";
import { AgentCard } from "./components/agent-card";
import { CreateAgentDialog } from "./components/create-dialog";

export function AgentsPage() {
  const { t } = useTranslation("agents");
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  const { data: agents = [], isLoading, isError, refetch } = useAgents();
  const { data: connectors = [] } = useConnectors(clientId);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  // Group agents by category
  const { associatedAgents, transferableAgents, otherAgents } = useMemo(() => {
    const agentIdToConnectors = new Map<string, typeof connectors>();

    // Map agents to their connectors
    connectors.forEach((connector) => {
      if (connector.agent_id) {
        const existing = agentIdToConnectors.get(connector.agent_id) || [];
        agentIdToConnectors.set(connector.agent_id, [...existing, connector]);
      }
    });

    const associated = agents.filter((agent) =>
      agentIdToConnectors.has(agent.id!),
    );
    const transferable = agents.filter(
      (agent) =>
        !agentIdToConnectors.has(agent.id!) && agent.can_receive_transfers,
    );
    const other = agents.filter(
      (agent) =>
        !agentIdToConnectors.has(agent.id!) && !agent.can_receive_transfers,
    );

    return {
      associatedAgents: associated.map((agent) => ({
        agent,
        connectors: agentIdToConnectors.get(agent.id!) || [],
      })),
      transferableAgents: transferable,
      otherAgents: other,
    };
  }, [agents, connectors]);

  return (
    <PageLayout
      title={t("agents.title")}
      description={t("agents.description")}
      actions={
        <Button onClick={() => setIsCreateOpen(true)}>
          <PlusIcon className="h-4 w-4" />
          {t("agents.createAgent")}
        </Button>
      }
    >
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-72 rounded-lg" />
          ))}
        </div>
      ) : isError ? (
        <ErrorState onRetry={refetch} />
      ) : agents.length === 0 ? (
        <EmptyState
          icon={RobotIcon}
          title={t("agents.empty")}
          action={
            <Button onClick={() => setIsCreateOpen(true)}>
              <PlusIcon className="h-4 w-4" />
              {t("agents.createFirstAgent")}
            </Button>
          }
        />
      ) : (
        // The lines take the primary colour through `currentColor`: an SVG
        // stroke attribute does not resolve a CSS variable. The cards reset
        // the text colour, and sit above the lines, which only show in the
        // gaps between rows. Below `sm` the cards stack in one column, where a
        // line would join two cards of the same row: the lines are hidden, and
        // the row headings say who receives transfers.
        <div className="text-primary [&>div>svg]:hidden sm:[&>div>svg]:block">
          <ConnectorContainer
            strokeColor="currentColor"
            strokeWidth={2}
            lineStyle="curve"
            svgContainerStyle={{ zIndex: 1 }}
          >
            <div className="relative z-10 space-y-10 text-foreground">
              <AgentRow title={t("overview.onChannels")}>
                {associatedAgents.length === 0 ? (
                  <Link
                    to={`/${clientId}/settings/connectors`}
                    className="flex h-full flex-col items-center justify-center rounded-lg border border-dashed p-6 text-center transition-colors hover:border-ring/40 hover:bg-muted/40"
                  >
                    <div className="mb-3 flex size-12 items-center justify-center rounded-full bg-info-soft text-info">
                      <PhoneIcon weight="duotone" className="size-6" />
                    </div>
                    <p className="mb-1 font-medium">
                      {t("overview.setupNumber")}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {t("overview.setupNumberHint")}
                    </p>
                  </Link>
                ) : (
                  associatedAgents.map(({ agent, connectors }) => (
                    <ConnectorElement
                      key={agent.id}
                      id={`connected-${agent.id}`}
                      relations={
                        // Agents that can be reached are the ones that
                        // answer a channel: each one may transfer to every
                        // agent accepting transfers.
                        transferableAgents.map((transferAgent) => ({
                          targetId: `transferable-${transferAgent.id}`,
                          targetAnchor: "top" as const,
                          sourceAnchor: "bottom" as const,
                        }))
                      }
                    >
                      <AgentCard agent={agent} connectors={connectors} />
                    </ConnectorElement>
                  ))
                )}
              </AgentRow>

              {transferableAgents.length > 0 && (
                <AgentRow
                  title={t("overview.receivesTransfers")}
                  description={
                    associatedAgents.length > 0
                      ? t("overview.receivesTransfersHint")
                      : undefined
                  }
                >
                  {transferableAgents.map((agent) => (
                    <ConnectorElement
                      key={agent.id}
                      id={`transferable-${agent.id}`}
                    >
                      <AgentCard agent={agent} />
                    </ConnectorElement>
                  ))}
                </AgentRow>
              )}

              {otherAgents.length > 0 && (
                <AgentRow title={t("overview.notAssociated")}>
                  {otherAgents.map((agent) => (
                    <AgentCard key={agent.id} agent={agent} />
                  ))}
                </AgentRow>
              )}
            </div>
          </ConnectorContainer>
        </div>
      )}

      <CreateAgentDialog open={isCreateOpen} onOpenChange={setIsCreateOpen} />
    </PageLayout>
  );
}

/**
 * One row of the overview. The heading sits on the page's own background, so
 * a transfer line running behind it stays readable around the words.
 */
function AgentRow({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3">
      <div className="w-fit bg-background pr-3">
        <h3 className="text-sm font-semibold">{title}</h3>
        {description && (
          <p className="text-xs text-muted-foreground">{description}</p>
        )}
      </div>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 [&>*]:h-full">
        {children}
      </div>
    </section>
  );
}
